import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const SignupPassword = ({ formData, errors, handleChange, isLoading, prevStep, handleSubmit }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const location = useLocation();
  
  // Password strength checker based on Google's recommendations
  const checkPasswordStrength = (password) => {
    if (!password) return { score: 0, message: 'Enter a password' };
    
    let score = 0;
    const messages = [];
    
    // Length check (Google recommends at least 8 characters)
    if (password.length >= 8) score += 1;
    else messages.push('At least 8 characters');
    
    // Uppercase letter check
    if (/[A-Z]/.test(password)) score += 1;
    else messages.push('One uppercase letter');
    
    // Lowercase letter check
    if (/[a-z]/.test(password)) score += 1;
    else messages.push('One lowercase letter');
    
    // Number check
    if (/\d/.test(password)) score += 1;
    else messages.push('One number');
    
    // Special character check
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    else messages.push('One special character');
    
    // Google's recommendation: Avoid common patterns
    const commonPatterns = ['123456', 'password', 'qwerty', 'abcdef', '111111'];
    if (!commonPatterns.some(pattern => password.toLowerCase().includes(pattern))) {
      score += 1;
    } else {
      messages.push('Avoid common patterns');
    }
    
    // Determine strength level
    let strength = '';
    if (score >= 5) {
      strength = 'Strong';
    } else if (score >= 3) {
      strength = 'Medium';
    } else {
      strength = 'Weak';
    }
    
    return {
      score,
      strength,
      message: messages.length > 0 ? messages.join(', ') : 'Strong password!',
      isStrong: score >= 5
    };
  };
  
  const passwordStrength = checkPasswordStrength(formData.password.newPassword);
  
  // Copy to clipboard function
  const copyToClipboard = (field) => {
    const text = field === 'new' ? formData.password.newPassword : formData.password.confirmPassword;
    if (!text) return;
    
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      alert(`Password copied to clipboard!`);
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };
  
  // Paste from clipboard function
  const pasteFromClipboard = (field) => {
    navigator.clipboard.readText().then(text => {
      if (text) {
        const event = {
          target: {
            name: field === 'new' ? 'password.newPassword' : 'password.confirmPassword',
            value: text
          }
        };
        handleChange(event);
      }
    }).catch(err => {
      console.error('Failed to paste: ', err);
    });
  };
  
  // Toggle password visibility
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };
  
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  return (
    <div className="space-y-4 lg:space-y-5">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">Create Password</h3>
        <p className="text-gray-600">Secure your account</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="group">
          <label htmlFor="password.newPassword" className="block text-sm font-semibold text-gray-700 mb-2">
            New Password *
          </label>
          <div className="relative">
            <input
              ref={newPasswordRef}
              id="password.newPassword"
              name="password.newPassword"
              type={showNewPassword ? "text" : "password"}
              required
              value={formData.password.newPassword}
              onChange={handleChange}
              className={`w-full px-4 py-3 text-base border-2 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm pr-16 ${
                errors['password.newPassword'] 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                  : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
              }`}
              placeholder="••••••••"
            />
            {/* Password strength indicator */}
            {formData.password.newPassword && (
              <div className="absolute top-1/2 right-10 transform -translate-y-1/2">
                <div className={`w-2 h-2 rounded-full ${
                  passwordStrength.strength === 'Strong' ? 'bg-green-500' :
                  passwordStrength.strength === 'Medium' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
              </div>
            )}
            {/* Eye toggle button */}
            <button
              type="button"
              onClick={toggleNewPasswordVisibility}
              className="absolute top-1/2 right-4 transform -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-300"
            >
              {showNewPassword ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Copy button below field */}
          {formData.password.newPassword && (
            <div className="mt-1 flex items-center space-x-2">
              <button
                type="button"
                onClick={() => copyToClipboard('new')}
                className="flex items-center space-x-1 text-xs text-gray-500 hover:text-green-600 transition-colors duration-300"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </button>
              
              <span className="text-gray-300">|</span>
              
              <button
                type="button"
                onClick={() => pasteFromClipboard('new')}
                className="flex items-center space-x-1 text-xs text-gray-500 hover:text-green-600 transition-colors duration-300"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Paste</span>
              </button>
            </div>
          )}
          
          {/* Password strength bar and message */}
          {formData.password.newPassword && (
            <div className="mt-2 space-y-1">
              <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((index) => (
                  <div
                    key={index}
                    className={`h-1 flex-1 rounded-full ${
                      index <= passwordStrength.score
                        ? passwordStrength.strength === 'Strong' ? 'bg-green-500' :
                          passwordStrength.strength === 'Medium' ? 'bg-yellow-500' : 'bg-red-500'
                        : 'bg-gray-200'
                    }`}
                  ></div>
                ))}
              </div>
              <p className={`text-xs ${
                passwordStrength.strength === 'Strong' ? 'text-green-600' :
                passwordStrength.strength === 'Medium' ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {passwordStrength.message}
              </p>
            </div>
          )}
          
          {errors['password.newPassword'] && (
            <p className="mt-1 text-sm text-red-600">{errors['password.newPassword']}</p>
          )}
        </div>

        <div className="group">
          <label htmlFor="password.confirmPassword" className="block text-sm font-semibold text-gray-700 mb-2">
            Confirm Password *
          </label>
          <div className="relative">
            <input
              ref={confirmPasswordRef}
              id="password.confirmPassword"
              name="password.confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              required
              value={formData.password.confirmPassword}
              onChange={handleChange}
              className={`w-full px-4 py-3 text-base border-2 rounded-xl placeholder-gray-400 focus:outline-none focus:ring-2 transition-all duration-300 bg-white shadow-sm pr-16 ${
                errors['password.confirmPassword'] 
                  ? 'border-red-300 focus:border-red-500 focus:ring-red-100' 
                  : 'border-gray-200 focus:border-green-500 focus:ring-green-100'
              }`}
              placeholder="••••••••"
            />
            {/* Password match indicator */}
            {formData.password.confirmPassword && (
              <div className="absolute top-1/2 right-10 transform -translate-y-1/2">
                {formData.password.newPassword === formData.password.confirmPassword ? (
                  <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            )}
            {/* Eye toggle button */}
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute top-1/2 right-4 transform -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-300"
            >
              {showConfirmPassword ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Copy/Paste buttons below field */}
          {formData.password.confirmPassword && (
            <div className="mt-1 flex items-center space-x-2">
              <button
                type="button"
                onClick={() => copyToClipboard('confirm')}
                className="flex items-center space-x-1 text-xs text-gray-500 hover:text-green-600 transition-colors duration-300"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Copy</span>
              </button>
              
              <span className="text-gray-300">|</span>
              
              <button
                type="button"
                onClick={() => pasteFromClipboard('confirm')}
                className="flex items-center space-x-1 text-xs text-gray-500 hover:text-green-600 transition-colors duration-300"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>Paste</span>
              </button>
            </div>
          )}
          
          {/* Password match message */}
          {formData.password.confirmPassword && formData.password.newPassword !== formData.password.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">Passwords do not match</p>
          )}
          
          {errors['password.confirmPassword'] && (
            <p className="mt-1 text-sm text-red-600">{errors['password.confirmPassword']}</p>
          )}
        </div>
      </div>

      {/* Password requirements section */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Password Requirements:</h4>
        <ul className="space-y-1 text-sm text-gray-600">
          <li className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${formData.password.newPassword?.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {formData.password.newPassword?.length >= 8 ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
              )}
            </svg>
            At least 8 characters long
          </li>
          <li className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${/[A-Z]/.test(formData.password.newPassword) ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {/[A-Z]/.test(formData.password.newPassword) ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
              )}
            </svg>
            Contains uppercase letter
          </li>
          <li className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${/[a-z]/.test(formData.password.newPassword) ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {/[a-z]/.test(formData.password.newPassword) ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
              )}
            </svg>
            Contains lowercase letter
          </li>
          <li className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${/\d/.test(formData.password.newPassword) ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {/\d/.test(formData.password.newPassword) ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
              )}
            </svg>
            Contains number
          </li>
          <li className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password.newPassword) ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {/[!@#$%^&*(),.?":{}|<>]/.test(formData.password.newPassword) ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
              )}
            </svg>
            Contains special character
          </li>
          <li className="flex items-center">
            <svg className={`w-4 h-4 mr-2 ${passwordStrength.isStrong ? 'text-green-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {passwordStrength.isStrong ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              ) : (
                <circle cx="12" cy="12" r="10" strokeWidth={2} />
              )}
            </svg>
            Avoid common patterns (123456, password, etc.)
          </li>
        </ul>
      </div>

      <div className="flex items-start space-x-3">
        <div className="flex items-center h-5 mt-0.5">
          <input
            id="agreeToTerms"
            name="agreeToTerms"
            type="checkbox"
            checked={formData.agreeToTerms}
            onChange={handleChange}
            className={`w-4 h-4 text-green-500 bg-white border-2 rounded focus:ring-1 focus:ring-green-500 focus:ring-offset-1 transition-all duration-300 ${
              errors.agreeToTerms ? 'border-red-300' : 'border-gray-300'
            }`}
          />
        </div>
        <label htmlFor="agreeToTerms" className="text-sm text-gray-700 cursor-pointer">
          I agree to the{' '}
          <Link 
            to="/terms" 
            state={{ from: location.pathname, step: 3, formData: formData }}
            className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 hover:underline"
          >
            Terms and Conditions
          </Link>{' '}
          and{' '}
          <Link 
            to="/privacy" 
            state={{ from: location.pathname, step: 3, formData: formData }}
            className="font-semibold text-green-600 hover:text-green-700 transition-colors duration-300 hover:underline"
          >
            Privacy Policy
          </Link>
        </label>
      </div>
      {errors.agreeToTerms && (
        <p className="text-sm text-red-600">{errors.agreeToTerms}</p>
      )}

      <div className="flex space-x-4">
        <button
          type="button"
          onClick={prevStep}
          className="flex-1 py-3 px-6 text-base font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={isLoading || !passwordStrength.isStrong || formData.password.newPassword !== formData.password.confirmPassword}
          onClick={handleSubmit}
          className="flex-1 py-3 px-6 text-base font-semibold text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Creating Account...' : 'Become an Instructor'}
        </button>
      </div>
    </div>
  );
};

export default SignupPassword;